import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, filter } from 'rxjs';

/**
 * Screens up to this width get the phone layout: the side menu becomes a drawer over the page (see
 * src/app/styles/mobile.scss). Keep in sync with `$mobile-breakpoint` in src/app/styles/_breakpoints.scss.
 */
export const MOBILE_LAYOUT_QUERY = '(max-width: 768px)';

/** The fas-admin layout parts this service drives; the library renders them, so they are found in the DOM. */
const LAYOUT_SELECTOR = 'lib-fas-admin';
const SIDEBAR_OPEN_SELECTOR = 'lib-fas-admin .side-bar.show';
const SIDEBAR_TOGGLE_SELECTOR = 'lib-fas-admin .top-bar .arrow-btn';
const SIDEBAR_SELECTOR = 'lib-fas-admin .side-bar';
const CONTENT_SELECTOR = 'lib-fas-admin .layout > .content';

/**
 * Phone behaviour of the side menu rendered by fas-admin. The library keeps the menu open by default and shows it
 * next to the page, which leaves a phone with a sliver of content; it also does not export the service holding the
 * open/closed state. So on a narrow screen this service:
 * - closes the menu as soon as the layout appears (and when the screen gets narrow, e.g. a tablet is rotated),
 * - closes it after a tap anywhere in it: an entry (also one pointing to the page already shown, or one not
 *   available yet), a section title, the empty space below,
 * - closes it on a tap on the dimmed page next to it (the dimming itself is CSS on the page area),
 * - scrolls the page area back to the top when another page opens: on a phone pages scroll as a whole, and the
 *   page area is the library's element that stays in place, so the next page would open scrolled down.
 * Closing goes through the library's own toggle button, so its state stays consistent. Started once from
 * AppComponent; runs outside the zone - the clicks it makes trigger change detection through the library's handler.
 */
@Injectable({ providedIn: 'root' })
export class MobileShellService implements OnDestroy {
  private readonly zone = inject(NgZone);
  private readonly router = inject(Router);
  private readonly listeners = new AbortController();
  private observer?: MutationObserver;
  private navigation?: Subscription;
  private started = false;

  start(): void {
    if (this.started || typeof window.matchMedia !== 'function') {
      return;
    }
    this.started = true;
    const mobile = window.matchMedia(MOBILE_LAYOUT_QUERY);
    const signal = this.listeners.signal;

    // Capture phase: a tap on the dimmed page must only close the menu, not reach the page under it.
    const onLayoutClick = (event: Event) => {
      if (!mobile.matches || !this.isSidebarOpen()) {
        return;
      }
      const target = event.target as Element | null;
      if (target?.matches(CONTENT_SELECTOR)) {
        event.preventDefault();
        event.stopPropagation();
        this.closeSidebar();
      } else if (target?.closest(SIDEBAR_SELECTOR)) {
        // Let the router handle the tap first, then fold the menu away.
        setTimeout(() => this.closeSidebar());
      }
    };

    this.zone.runOutsideAngular(() => {
      // The layout is created after login and after a reload, with the menu open. As soon as it appears:
      // - the click listener goes on the layout element itself, not on the document: iOS Safari turns a tap into a
      //   click only on elements that listen to clicks, or have an ancestor below <body> that does - otherwise a tap
      //   on a section title or on the dimmed page would do nothing;
      // - on a phone the menu is closed before the first paint (mutation callbacks run before rendering).
      let layout: Element | null = null;
      const syncLayout = () => {
        const current = document.querySelector(SIDEBAR_TOGGLE_SELECTOR)?.closest(LAYOUT_SELECTOR) ?? null;
        if (current && current !== layout) {
          current.addEventListener('click', onLayoutClick, { capture: true, signal });
          if (mobile.matches) {
            this.closeSidebar();
          }
        }
        layout = current;
      };
      this.observer = new MutationObserver(syncLayout);
      this.observer.observe(document.body, { childList: true, subtree: true });
      syncLayout();

      mobile.addEventListener(
        'change',
        () => {
          if (mobile.matches) {
            this.closeSidebar();
          }
        },
        { signal }
      );
    });

    let previousPath = pathOf(this.router.url);
    this.navigation = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        const path = pathOf(event.urlAfterRedirects);
        // Query parameters only (filters, a selected tab kept in the URL) leave the scroll position alone.
        if (path !== previousPath && mobile.matches) {
          document.querySelector(CONTENT_SELECTOR)?.scrollTo({ top: 0, behavior: 'instant' });
        }
        previousPath = path;
      });
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.navigation?.unsubscribe();
    this.listeners.abort();
  }

  private isSidebarOpen(): boolean {
    return document.querySelector(SIDEBAR_OPEN_SELECTOR) !== null;
  }

  private closeSidebar(): void {
    if (this.isSidebarOpen()) {
      document.querySelector<HTMLButtonElement>(SIDEBAR_TOGGLE_SELECTOR)?.click();
    }
  }
}

/** The URL without its query string and fragment. */
function pathOf(url: string): string {
  return url.split(/[?#]/, 1)[0];
}
